/**
 * High-level offline DB operations.
 *
 * The Dexie singleton in `./schema` exposes the raw tables; this file builds
 * on top of it for the cases where the app needs to write to more than one
 * table atomically — e.g. saving a visit and its vitals must either both
 * succeed or both fail, otherwise the local DB ends up with a visit row that
 * has no matching vitals row.
 *
 * Each helper here:
 *   1. Wraps the writes in a single Dexie `db.transaction("rw", ...)` call.
 *   2. Stamps `sync_status="pending"` and `local_updated_at=now` on every row.
 *   3. Enqueues the corresponding sync_queue entries inside the same transaction
 *      so a crash mid-write can never leave a record without its queue op.
 */

import type { Table } from "dexie";

import { db } from "./schema";
import { wrapForStorage } from "./encryption-bridge";
import { enqueue } from "../services/offline-sync.service";
import type {
  EntityType,
  SyncFields,
  SyncOperation,
  VisitRecord,
  VitalsRecord,
} from "./db.types";

// ─── Internal helpers ────────────────────────────────────────────────────────

function stampSyncFields<T>(record: T, version: number): T & SyncFields {
  return {
    ...record,
    sync_status:      "pending",
    version,
    local_updated_at: new Date().toISOString(),
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Save a visit and its vitals atomically. Either both writes succeed or
 * neither does — guarantees the local DB never has a half-written state.
 *
 * @param visit       visit record (without sync fields — they're stamped here)
 * @param vitals      vitals record (without sync fields), or null if none
 * @param operation   REQUIRED — "create" for new records, "update" for edits.
 *                    No default: a forgotten "create" would enqueue an update
 *                    against a server row that doesn't exist (404 on flush).
 * @param syncPayload Optional override for the queue payload. Use when the
 *                    server needs fields not stored in the Dexie record (e.g.
 *                    nested vitals / visit_type in POST /visits). Defaults to
 *                    the visit record itself.
 */
export async function saveVisitWithVitals(
  visit:        Omit<VisitRecord,  keyof SyncFields>,
  vitals:       Omit<VitalsRecord, keyof SyncFields> | null,
  operation:    SyncOperation,
  syncPayload?: Record<string, unknown>,
): Promise<void> {
  // Encrypt OUTSIDE the Dexie transaction. Web Crypto operations return
  // promises that don't participate in Dexie's transaction lifecycle, so
  // awaiting them inside `db.transaction(...)` would auto-commit early.
  const stampedVisit  = stampSyncFields(visit,  visit.sync_version ?? 1);
  const visitEnvelope = await wrapForStorage("visit", stampedVisit as unknown as Record<string, unknown>);

  let vitalsEnvelope: Record<string, unknown> | null = null;
  let stampedVitalsVersion = 0;
  if (vitals) {
    const stampedVitals  = stampSyncFields(vitals, 1);
    stampedVitalsVersion = stampedVitals.version;
    vitalsEnvelope       = await wrapForStorage("vitals", stampedVitals as unknown as Record<string, unknown>);
  }

  await db.transaction("rw", db.visits, db.vitals, db.sync_queue, async () => {
    await db.visits.put(visitEnvelope as never);
    await enqueue(
      operation, "visit", visit.visit_id,
      syncPayload ?? (visit as Record<string, unknown>),
      stampedVisit.version,
    );

    if (vitalsEnvelope) {
      // Vitals don't have a server sync endpoint of their own — they ride
      // along with the visit's sync. Don't enqueue a separate vitals op.
      await db.vitals.put(vitalsEnvelope as never);
      void stampedVitalsVersion; // explicit no-use; kept for symmetry/debug.
    }
  });
}

/**
 * Generic single-record offline write: stamp sync fields, put the row, and
 * enqueue the sync op — all in one transaction. Use directly from services
 * that don't need cross-table atomicity (the visit + vitals case above).
 *
 * `operation` is REQUIRED — no default. A caller saving a brand-new record
 * who forgets to pass "create" would otherwise enqueue an "update" against
 * a server row that doesn't exist (404 on flush, silent sync failure).
 */
export async function putAndQueue<T extends SyncFields>(
  entityType:  EntityType,
  entityId:    string,
  record:      Omit<T, keyof SyncFields>,
  operation:   SyncOperation,
  version:     number = 1,
  // Optional override for the sync-queue payload. Use when the server needs
  // fields not present in the Dexie record type (e.g. nested contact/address on
  // patient CREATE). If omitted, `record` is used as the queue payload too.
  syncPayload?: Record<string, unknown>,
): Promise<void> {
  const table   = tableFor(entityType);
  const stamped = stampSyncFields(record, version) as T;
  // Encrypt before opening the Dexie transaction (Web Crypto promises don't
  // play well with Dexie's transaction lifecycle — see saveVisitWithVitals).
  const envelope = await wrapForStorage(entityType, stamped as unknown as Record<string, unknown>);

  await db.transaction("rw", table, db.sync_queue, async () => {
    await table.put(envelope as never);
    await enqueue(operation, entityType, entityId, syncPayload ?? record as Record<string, unknown>, version);
  });
}

// Map an EntityType to the corresponding Dexie table. Centralised so future
// entity types are added in exactly one place.
//
// Return type is widened to `Table<any, string>` deliberately: each table's
// record type has a distinct `status` field (VisitStatus vs AppointmentStatus
// vs PrescriptionStatus etc.), so a precise union intersects to `never` when
// `.put()` is called from generic code. Runtime is unchanged — Dexie does no
// type-time checks.
function tableFor(entityType: EntityType): Table<any, string> {
  switch (entityType) {
    case "patient":      return db.patients;
    case "visit":        return db.visits;
    case "vitals":       return db.vitals;
    case "prescription": return db.prescriptions;
    case "appointment":  return db.appointments;
    case "allergy":      return db.allergies;
    // doctor: no local Dexie table yet — managed server-side only.
    // putAndQueue isn't called for "doctor" today; if/when a doctors
    // table is added to schema.ts, wire the case here.
    case "doctor":       throw new Error("doctor entity has no local Dexie table");
  }
}
